import React, { useEffect, useState } from 'react';
import relatorioService, { type PrestacaoRelatoriosCabecalho } from '../../services/relatorioPrestacaoContasService';
import { type PrestacaoPc } from '../../services/prestacaoContaService';
import Anexo2ExecucaoFisicaSection from './Anexo2ExecucaoFisicaSection';
import Anexo3RelatoNarrativoSection from './Anexo3RelatoNarrativoSection';
import Anexo5PagamentosSection from './Anexo5PagamentosSection';
import Anexo6AlcanceMetasSection from './Anexo6AlcanceMetasSection';
import Anexo8RemuneracaoEquipeSection from './Anexo8RemuneracaoEquipeSection';
import PrestacaoBensSection from './PrestacaoBensSection';
import PrestacaoMetasExecucaoSection from './PrestacaoMetasExecucaoSection';
import PrestacaoExecucaoFinanceiraTable from './PrestacaoExecucaoFinanceiraTable';
import PrestacaoExtratosSection from './PrestacaoExtratosSection';
import PrestacaoFotosSection from './PrestacaoFotosSection';
import PrestacaoDespesasRhAluguelSection from './PrestacaoDespesasRhAluguelSection';
import {
  bodyTextClass, formatDate, isSaas, labelTextClass, pcLabel,
  strongTextClass, subtleSurfaceClass, surfaceClass,
} from './shared';

export type Anexo = '2' | '3' | '4' | '5' | '6' | '7' | '8';
type Aba = 'metas' | 'financeiro' | 'fotos' | 'despesas' | Anexo;

export const ANEXOS: { key: Anexo; label: string }[] = [
  { key: '2', label: 'Anexo 2 · Execução Físico-Financeira' },
  { key: '3', label: 'Anexo 3 · Cumprimento/Execução do Objeto' },
  { key: '4', label: 'Anexo 4 · Bens Adquiridos' },
  { key: '5', label: 'Anexo 5 · Pagamentos Realizados' },
  { key: '6', label: 'Anexo 6 · Relatório Final (metas)' },
  { key: '7', label: 'Anexo 7 · Devolução de Bens' },
  { key: '8', label: 'Anexo VIII · Publicidade' },
];

type Props = {
  planoId: string;
  pcs: PrestacaoPc[];
  podeEditar: boolean;
  onCabecalhoCarregado?: (cabecalho: PrestacaoRelatoriosCabecalho) => void;
  onAlterado?: () => void;
};

/**
 * Navegação + conteúdo da prestação de contas de um plano, organizada por
 * PC (peça de prestação de contas) — única ou parcial, nunca as duas juntas.
 * Os anexos 2/3/4/6/7/8 são por PC (o processo oficial presta contas PC a
 * PC), por isso ganham um seletor de PC quando há mais de uma. "Metas e
 * Etapas Executadas" e os 7 anexos são por PC. "Execução Financeira" reúne
 * a tabela encadeada do plano e os comprovantes da PC selecionada.
 */
const PrestacaoContasAnexosPanel: React.FC<Props> = ({
  planoId, pcs, podeEditar, onCabecalhoCarregado, onAlterado,
}) => {
  const [cabecalhos, setCabecalhos] = useState<Record<string, PrestacaoRelatoriosCabecalho>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ativo, setAtivo] = useState<Aba>('metas');
  const [pcAtiva, setPcAtiva] = useState<string>('');
  const [financeiroVersao, setFinanceiroVersao] = useState(0);

  useEffect(() => {
    if (pcs.length === 0) return;
    let ativoEfeito = true;
    setLoading(true);
    setError(null);
    Promise.all(pcs.map((pc) => relatorioService.getCabecalho(pc.pcId).then((data) => [pc.pcId, data] as const)))
      .then((entries) => {
        if (!ativoEfeito) return;
        const porPc = Object.fromEntries(entries);
        setCabecalhos(porPc);
        const preferida = pcs.find((pc) => pc.tipo === 'UNICA')?.pcId || pcs[0].pcId;
        setPcAtiva((atual) => (atual && porPc[atual] ? atual : preferida));
        const geral = porPc[preferida];
        if (geral) onCabecalhoCarregado?.(geral);
      })
      .catch((err) => { if (ativoEfeito) setError(err?.response?.data?.error || 'Falha ao carregar os dados da parceria.'); })
      .finally(() => { if (ativoEfeito) setLoading(false); });
    return () => { ativoEfeito = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcs.map((pc) => pc.pcId).join(',')]);

  const navItemClass = (key: Aba) => {
    const active = ativo === key;
    if (active) {
      return isSaas
        ? 'bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300'
        : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
    }
    return 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800';
  };

  const cabecalhoAtivo = cabecalhos[pcAtiva];
  const pcAtivaDto = pcs.find((pc) => pc.pcId === pcAtiva);
  const situacaoEditavel = !pcAtivaDto || ['PENDENTE', 'RASCUNHO', 'EM_CORRECAO'].includes(pcAtivaDto.situacao);
  const podeEditarPcAtiva = podeEditar && pcAtivaDto?.statusPrestacao !== 'FECHADA' && !pcAtivaDto?.prazoExpirado && situacaoEditavel;
  const desabilitado = (anexo: Anexo) => anexo === '6' && cabecalhoAtivo != null && !cabecalhoAtivo.ehPcFinal;
  const fotosDisponiveis = cabecalhoAtivo == null || cabecalhoAtivo.ehPcFinal;
  const despesasDisponiveis = pcAtivaDto?.exigeDeclaracaoDespesas ?? true;

  if (pcs.length === 0) {
    return (
      <div className={`rounded-2xl border p-4 text-sm ${surfaceClass} ${bodyTextClass}`}>
        Nenhuma PC criada ainda para este plano. Crie uma PC acima para começar a preencher os anexos.
      </div>
    );
  }

  if (loading) {
    return <div className={`rounded-2xl border p-4 text-sm ${surfaceClass} ${bodyTextClass}`}>Carregando anexos...</div>;
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
        {error}
      </div>
    );
  }

  const cabecalhoGeral = cabecalhos[pcs.find((pc) => pc.tipo === 'UNICA')?.pcId || pcs[0]?.pcId || ''];
  if (!cabecalhoGeral) return null;

  return (
    <div className="space-y-4">
      <div className={`grid grid-cols-1 gap-4 overflow-hidden rounded-2xl border p-4 text-sm sm:grid-cols-2 lg:grid-cols-4 ${subtleSurfaceClass}`}>
        <div><p className={`text-xs uppercase ${labelTextClass}`}>Organização</p><p className={strongTextClass}>{cabecalhoGeral.instituicaoNome || '-'}</p></div>
        <div><p className={`text-xs uppercase ${labelTextClass}`}>CNPJ</p><p className={strongTextClass}>{cabecalhoGeral.instituicaoCnpj || '-'}</p></div>
        <div><p className={`text-xs uppercase ${labelTextClass}`}>Responsável</p><p className={strongTextClass}>{cabecalhoGeral.dirigenteNome || '-'}</p></div>
        <div><p className={`text-xs uppercase ${labelTextClass}`}>Vigência</p><p className={strongTextClass}>{formatDate(cabecalhoGeral.periodoInicio)} a {formatDate(cabecalhoGeral.periodoFim)}</p></div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className={`h-fit space-y-1 rounded-2xl border p-2 ${surfaceClass}`}>
          <button
            type="button"
            onClick={() => setAtivo('metas')}
            className={`block w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${navItemClass('metas')}`}
          >
            Metas e Etapas Executadas
          </button>
          <button
            type="button"
            onClick={() => setAtivo('financeiro')}
            className={`block w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${navItemClass('financeiro')}`}
          >
            Execução Financeira
          </button>
          <div className="my-1 border-t border-slate-200 dark:border-slate-800" />
          {ANEXOS.map((a) => {
            const disabled = desabilitado(a.key);
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => !disabled && setAtivo(a.key)}
                disabled={disabled}
                title={disabled ? 'Disponível apenas na PC final/única' : undefined}
                className={`block w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                  disabled ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed' : navItemClass(a.key)
                }`}
              >
                {a.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => fotosDisponiveis && setAtivo('fotos')}
            disabled={!fotosDisponiveis}
            title={!fotosDisponiveis ? 'Disponível apenas na PC final/única' : undefined}
            className={`block w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
              !fotosDisponiveis ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed' : navItemClass('fotos')
            }`}
          >
            Relatório fotográfico
          </button>
          <button
            type="button"
            onClick={() => despesasDisponiveis && setAtivo('despesas')}
            disabled={!despesasDisponiveis}
            title={!despesasDisponiveis ? 'Cobrado apenas na primeira PC ou PC única' : undefined}
            className={`block w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
              !despesasDisponiveis ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed' : navItemClass('despesas')
            }`}
          >
            Despesas com RH e aluguel
          </button>
        </nav>

        <div className={`space-y-4 rounded-2xl border p-5 ${surfaceClass}`}>
          {pcs.length > 1 && (
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3 dark:border-slate-800">
                  <label htmlFor="anexo-pc-ativa" className={`text-xs font-semibold uppercase tracking-[0.14em] ${labelTextClass}`}>
                    PC
                  </label>
                  <select
                    id="anexo-pc-ativa"
                    value={pcAtiva}
                    onChange={(e) => setPcAtiva(e.target.value)}
                    className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                  >
                    {pcs.map((pc) => (
                      <option key={pc.pcId} value={pc.pcId}>{pcLabel(pc)}</option>
                    ))}
                  </select>
                </div>
          )}

          {ativo === 'financeiro' && (
            <>
              <PrestacaoExecucaoFinanceiraTable planoId={planoId} pcs={pcs} reloadKey={financeiroVersao} />
              <div className="border-t border-slate-200 pt-5 dark:border-slate-800">
                <PrestacaoExtratosSection key={pcAtiva} pcId={pcAtiva} podeEditar={pcAtivaDto?.podeAdicionarComprovante ?? false}
                  onChange={() => { setFinanceiroVersao((v) => v + 1); onAlterado?.(); }} />
              </div>
            </>
          )}

          {ativo !== 'financeiro' && (
            <>
              {ativo === 'metas' && (
                <PrestacaoMetasExecucaoSection
                  key={pcAtiva}
                  pcId={pcAtiva}
                  podeEditar={podeEditarPcAtiva}
                  podeAdicionarComprovante={pcAtivaDto?.podeAdicionarComprovante ?? false}
                  onAlterado={onAlterado}
                />
              )}
              {ativo === '2' && <Anexo2ExecucaoFisicaSection pcId={pcAtiva} podeEditar={podeEditarPcAtiva} />}
              {ativo === '3' && cabecalhoAtivo && <Anexo3RelatoNarrativoSection pcId={pcAtiva} podeEditar={podeEditarPcAtiva} cabecalho={cabecalhoAtivo} onAlterado={onAlterado} />}
              {ativo === '4' && <PrestacaoBensSection key={pcAtiva} pcId={pcAtiva} podeEditar={podeEditarPcAtiva && Boolean(cabecalhoAtivo?.temItemInvestimento)} situacao="ADQUIRIDO" onAlterado={onAlterado} />}
              {ativo === '4' && !cabecalhoAtivo?.temItemInvestimento && <p className={`text-sm ${labelTextClass}`}>O cadastro de bens se aplica apenas a planos com investimento.</p>}
              {ativo === '5' && (
                <Anexo5PagamentosSection
                  key={pcAtiva}
                  pcId={pcAtiva}
                />
              )}
              {ativo === '6' && <Anexo6AlcanceMetasSection pcId={pcAtiva} podeEditar={podeEditarPcAtiva} onAlterado={onAlterado} />}
              {ativo === '7' && <PrestacaoBensSection pcId={pcAtiva} podeEditar={podeEditarPcAtiva} situacao="DEVOLVIDO" />}
              {ativo === '8' && <Anexo8RemuneracaoEquipeSection pcId={pcAtiva} podeEditar={podeEditarPcAtiva} />}
              {ativo === 'fotos' && <PrestacaoFotosSection key={pcAtiva} pcId={pcAtiva} podeEditar={podeEditarPcAtiva} onAlterado={onAlterado} />}
              {ativo === 'despesas' && (
                <PrestacaoDespesasRhAluguelSection
                  key={pcAtiva}
                  pcId={pcAtiva}
                  podeEditar={podeEditarPcAtiva}
                  declaraRh={pcAtivaDto?.declaraDespesaRh}
                  declaraAluguel={pcAtivaDto?.declaraDespesaAluguel}
                  onAlterado={onAlterado}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrestacaoContasAnexosPanel;
